// ── Escritor mínimo de .xlsx ──────────────────────────────────────
// Gera UMA aba a partir de cabeçalho + linhas. Só isso — não lê, não
// calcula fórmula, não desenha gráfico.
//
// É o espelho do leitor em src/infra/parsers/xlsxGrid.ts e vale a mesma
// justificativa: o pacote `xlsx` do npm parou em 0.18.5 com CVE-2023-30533
// e CVE-2024-22363, e a SheetJS saiu do registro. `fflate` (~8 KB) compacta
// o ZIP e o XML é string — o custo é este arquivo.
//
// O fflate entra por import() dinâmico: quem só navega pela tela não baixa
// o compactador, ele chega quando alguém clica em exportar.

/** Texto vira célula de texto; número, célula numérica; Date, célula de data. */
export type CelulaXlsx = string | number | Date | null | undefined;

export interface PlanilhaXlsx {
  /** Nome da aba. Excel limita a 31 caracteres e proíbe []:*?/\ */
  aba: string;
  colunas: string[];
  linhas: CelulaXlsx[][];
}

// ── XML ───────────────────────────────────────────────────────────

// & < > " e os caracteres de controle que o Excel recusa no XML. Eles
// entram por colagem de PDF/WhatsApp em nome ou observação e derrubariam o
// arquivo inteiro na abertura, então somem aqui.
const esc = (texto: string): string =>
  texto
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const CABECALHO_XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

// ── Referência de célula (coluna 0 → A) ───────────────────────────

const refDeColuna = (indice: number): string => {
  let n = indice + 1;
  let ref = '';
  while (n > 0) {
    const resto = (n - 1) % 26;
    ref = String.fromCharCode(65 + resto) + ref;
    n = Math.floor((n - resto) / 26);
  }
  return ref;
};

// ── Datas ─────────────────────────────────────────────────────────

/**
 * Date → serial do Excel. A época é 1899-12-30 (o Excel herdou do Lotus
 * 1-2-3 a crença de que 1900 foi bissexto; os dois dias de deslocamento
 * corrigem qualquer data depois de 01/03/1900).
 *
 * Os componentes saem em hora local e entram como UTC de propósito: o que
 * a planilha precisa mostrar é o dia que o usuário viu na tela, não o dia
 * em Greenwich.
 */
const serialDeData = (d: Date): number => {
  const ms = Date.UTC(
    d.getFullYear(), d.getMonth(), d.getDate(),
    d.getHours(), d.getMinutes(), d.getSeconds(),
  );
  return (ms - Date.UTC(1899, 11, 30)) / 86400000;
};

// ── Estilos ───────────────────────────────────────────────────────
// s=0 normal · s=1 cabeçalho em negrito · s=2 data dd/mm/aaaa

const ESTILO_PADRAO = 0;
const ESTILO_CABECALHO = 1;
const ESTILO_DATA = 2;

const STYLES_XML = `${CABECALHO_XML}
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="dd/mm/yyyy"/></numFmts>
<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

// ── Células ───────────────────────────────────────────────────────

const celulaXml = (valor: CelulaXlsx, ref: string, estilo: number): string => {
  if (valor == null || valor === '') return '';

  if (valor instanceof Date) {
    if (Number.isNaN(valor.getTime())) return '';
    return `<c r="${ref}" s="${ESTILO_DATA}"><v>${serialDeData(valor)}</v></c>`;
  }
  if (typeof valor === 'number') {
    if (!Number.isFinite(valor)) return '';
    return `<c r="${ref}" s="${estilo}"><v>${valor}</v></c>`;
  }
  // inlineStr em vez de sharedStrings: a tabela é gerada uma vez e lida
  // uma vez, então a tabela de strings compartilhadas só economizaria
  // bytes antes do gzip do ZIP — e custaria um passe a mais no código.
  // xml:space="preserve" mantém espaço no começo/fim do texto.
  return `<c r="${ref}" s="${estilo}" t="inlineStr"><is><t xml:space="preserve">${esc(valor)}</t></is></c>`;
};

const linhaXml = (celulas: CelulaXlsx[], iLinha: number, estilo: number): string => {
  const conteudo = celulas
    .map((v, i) => celulaXml(v, `${refDeColuna(i)}${iLinha}`, estilo))
    .join('');
  return `<row r="${iLinha}">${conteudo}</row>`;
};

// ── Largura das colunas ───────────────────────────────────────────

// O Excel abre toda coluna com a mesma largura e quem exporta acaba
// arrastando divisória a divisória. Medir o conteúdo resolve — largura em
// "caracteres", com piso no cabeçalho e teto para não estourar a tela.
const larguraDeColuna = (titulo: string, valores: CelulaXlsx[]): number => {
  const tamanho = (v: CelulaXlsx): number => {
    if (v == null) return 0;
    if (v instanceof Date) return 10;                 // dd/mm/aaaa
    return String(v).length;
  };
  const maior = valores.reduce<number>((max, v) => Math.max(max, tamanho(v)), titulo.length);
  return Math.min(Math.max(maior + 2, 10), 50);
};

// ── Nome da aba ───────────────────────────────────────────────────

// Fora de []:*?/\ o Excel ainda recusa nome que começa ou termina com
// apóstrofo — é o caractere com que ele cita a aba nas fórmulas.
const nomeDeAba = (nome: string): string => {
  const limpo = nome
    .replace(/[[\]:*?/\\]/g, ' ')
    .trim()
    .slice(0, 31)
    .replace(/^'+|'+$/g, '')
    .trim();
  return limpo || 'Planilha1';
};

// ── Montagem ──────────────────────────────────────────────────────

const sheetXml = ({ colunas, linhas }: PlanilhaXlsx): string => {
  // A largura sai da linha mais larga, não do cabeçalho: linha com mais
  // células que o cabeçalho deixaria `dimension` e `autoFilter` mais
  // estreitos que o conteúdo — células fora do range declarado, que o
  // Excel tolera mas nem todo leitor tolera.
  const largura = linhas.reduce<number>((max, l) => Math.max(max, l.length), colunas.length);
  const ultimaColuna = refDeColuna(Math.max(largura - 1, 0));
  const intervalo = `A1:${ultimaColuna}${linhas.length + 1}`;

  // `<cols/>` vazio é inválido no schema — sem coluna, o elemento não vai.
  const cols = colunas.length === 0 ? '' : `<cols>${colunas
    .map((titulo, i) => {
      const largura = larguraDeColuna(titulo, linhas.map(l => l[i]));
      return `<col min="${i + 1}" max="${i + 1}" width="${largura}" customWidth="1"/>`;
    })
    .join('')}</cols>`;

  const corpo = [
    linhaXml(colunas, 1, ESTILO_CABECALHO),
    ...linhas.map((l, i) => linhaXml(l, i + 2, ESTILO_PADRAO)),
  ].join('');

  // A ordem dos elementos é a do schema OOXML (dimension → sheetViews →
  // cols → sheetData → autoFilter); fora dela o Excel acusa arquivo
  // corrompido. O pane congela o cabeçalho ao rolar.
  return `${CABECALHO_XML}
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="${intervalo}"/>
<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
${cols}
<sheetData>${corpo}</sheetData>
<autoFilter ref="${intervalo}"/>
</worksheet>`;
};

const CONTENT_TYPES_XML = `${CABECALHO_XML}
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

const ROOT_RELS_XML = `${CABECALHO_XML}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const WORKBOOK_RELS_XML = `${CABECALHO_XML}
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

const workbookXml = (aba: string): string => `${CABECALHO_XML}
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${esc(nomeDeAba(aba))}" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

export const MIME_XLSX =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Monta os bytes do .xlsx. */
export const montarXlsx = async (planilha: PlanilhaXlsx): Promise<Uint8Array> => {
  const { zipSync, strToU8 } = await import('fflate');

  const arquivos: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(CONTENT_TYPES_XML),
    '_rels/.rels': strToU8(ROOT_RELS_XML),
    'xl/workbook.xml': strToU8(workbookXml(planilha.aba)),
    'xl/_rels/workbook.xml.rels': strToU8(WORKBOOK_RELS_XML),
    'xl/styles.xml': strToU8(STYLES_XML),
    'xl/worksheets/sheet1.xml': strToU8(sheetXml(planilha)),
  };

  return zipSync(arquivos, { level: 6 });
};

/** Monta o .xlsx e dispara o download. `nomeArquivo` sem extensão. */
export const baixarXlsx = async (
  nomeArquivo: string,
  planilha: PlanilhaXlsx,
): Promise<void> => {
  const blob = new Blob([await montarXlsx(planilha)], { type: MIME_XLSX });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nomeArquivo}.xlsx`;
  a.click();
  // Revogar na mesma volta do laço de eventos cancela o download antes de
  // o navegador ler o blob (Firefox e Safari já fizeram isso), e a chance
  // cresce com o tamanho do arquivo — uma base inteira de leads não é o
  // CSV de duas linhas do modelo de importação.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
